/**
 * DEMS Notification Framework - Type Definitions
 * 
 * This file contains all TypeScript interfaces and types for the notification system.
 * See docs/architecture/notifications-framework.md for full architecture documentation.
 */

// =============================================
// NOTIFICATION TYPES & ENUMS
// =============================================

/**
 * Notification categories aligned with DEMS domains
 */
export const NotificationCategory = {
  Evidence: 'Evidence',
  Case: 'Case',
  LegalHold: 'LegalHold',
  Discovery: 'Discovery',
  Compliance: 'Compliance',
  System: 'System',
} as const;

export type NotificationCategory = (typeof NotificationCategory)[keyof typeof NotificationCategory];

/**
 * Priority levels with associated SLA
 */
export const NotificationPriority = {
  Low: 'Low',
  Medium: 'Medium',
  High: 'High',
  Critical: 'Critical',
} as const;

export type NotificationPriority = (typeof NotificationPriority)[keyof typeof NotificationPriority];

/**
 * Delivery channels available
 */
export const NotificationChannel = {
  InApp: 'InApp',
  Email: 'Email',
  Push: 'Push',
} as const;

export type NotificationChannel = (typeof NotificationChannel)[keyof typeof NotificationChannel];

/**
 * Notification state lifecycle
 */
export const NotificationState = {
  Pending: 'Pending',
  Sent: 'Sent',
  Delivered: 'Delivered',
  Read: 'Read',
  Acknowledged: 'Acknowledged',
  Expired: 'Expired',
  Failed: 'Failed',
} as const;

export type NotificationState = (typeof NotificationState)[keyof typeof NotificationState];

/**
 * All notification type codes in DEMS
 */
export type NotificationType =
  // Evidence Events
  | 'EVIDENCE_UPLOADED'
  | 'EVIDENCE_UPLOAD_FAILED'
  | 'EVIDENCE_HASH_VERIFIED'
  | 'EVIDENCE_HASH_MISMATCH'
  | 'EVIDENCE_ACCESSED'
  | 'EVIDENCE_DOWNLOADED'
  | 'EVIDENCE_SHARED'
  | 'EVIDENCE_REDACTED'
  | 'EVIDENCE_PENDING_CLASSIFICATION'
  // Case Events
  | 'CASE_ASSIGNED'
  | 'CASE_STATUS_CHANGED'
  | 'CASE_DEADLINE_APPROACHING'
  | 'CASE_FILING_STATUS_CHANGED'
  // Legal Hold Events
  | 'LEGAL_HOLD_APPLIED'
  | 'LEGAL_HOLD_RELEASED'
  | 'LEGAL_HOLD_EXPIRING'
  // Discovery Events
  | 'DISCOVERY_REQUEST_RECEIVED'
  | 'DISCOVERY_REQUEST_APPROVED'
  | 'DISCOVERY_REQUEST_DENIED'
  | 'DISCOVERY_EVIDENCE_AVAILABLE'
  // Compliance Events
  | 'RETENTION_APPROACHING'
  | 'DISPOSITION_REVIEW_REQUIRED'
  | 'COMPLIANCE_AUDIT_REQUIRED'
  // Public Submission Events
  | 'SUBMISSION_RECEIVED'
  | 'SUBMISSION_UNDER_REVIEW'
  | 'SUBMISSION_ACCEPTED'
  | 'SUBMISSION_REJECTED'
  | 'SUBMISSION_INFO_REQUESTED'
  // Security Events
  | 'SECURITY_UNAUTHORIZED_ACCESS'
  | 'SECURITY_DELETE_ATTEMPT'
  | 'SECURITY_WORM_VIOLATION'
  | 'SECURITY_AUDIT_ANOMALY'
  // System Events
  | 'SYSTEM_HEALTH_DEGRADED'
  | 'SYSTEM_STORAGE_THRESHOLD'
  | 'SYSTEM_CERTIFICATE_EXPIRING'
  | 'SYSTEM_BACKUP_STATUS';

// =============================================
// CORE NOTIFICATION MODEL
// =============================================

/**
 * Core notification entity
 */
export interface Notification {
  /** Unique notification ID (UUID) */
  id: string;
  
  /** Notification type code */
  type: NotificationType;
  
  /** Category for filtering */
  category: NotificationCategory;
  
  /** Priority level */
  priority: NotificationPriority;
  
  /** Short title for display */
  title: string;
  
  /** Detailed message body (supports Markdown) */
  body: string;
  
  /** Current notification state */
  state?: NotificationState;
  
  /** Associated evidence ID (if applicable) */
  evidenceId?: string;
  
  /** Associated case number (if applicable) */
  caseNumber?: string;
  
  /** Deep link to relevant resource */
  actionUrl?: string;
  
  /** Action button label */
  actionLabel?: string;
  
  /** Additional structured data */
  metadata?: Record<string, unknown>;
  
  /** ISO timestamp when notification was created */
  createdAt: string;
  
  /** Expiration time for acknowledgment (critical notifications) */
  expiresAt?: string;
  
  /** Actor who triggered the notification (if any) */
  triggeredBy?: {
    userId: string;
    name: string;
    role: string;
  };
}

/**
 * Notification targeting - who should receive it
 */
export interface NotificationTarget {
  /** Notification ID this target belongs to */
  notificationId: string;
  
  /** Target user ID */
  userId: string;
  
  /** User's role (for filtering) */
  userRole: string;
  
  /** User's email (for email delivery) */
  userEmail: string;
  
  /** Delivery channels for this recipient */
  channels: NotificationChannel[];
  
  /** Whether acknowledgment is required */
  requiresAcknowledgment: boolean;
}

/**
 * Per-channel delivery tracking
 */
export interface NotificationDelivery {
  /** Unique delivery record ID */
  id: string;
  
  /** Notification ID */
  notificationId: string;
  
  /** Target user ID */
  userId: string;
  
  /** Delivery channel */
  channel: NotificationChannel;
  
  /** Current state */
  state: NotificationState;
  
  /** ISO timestamp of last state change */
  stateChangedAt: string;
  
  /** External delivery ID (SendGrid message ID, etc.) */
  externalId?: string;
  
  /** Error message if failed */
  errorMessage?: string;
  
  /** Retry count */
  retryCount: number;
  
  /** Next retry time (if applicable) */
  nextRetryAt?: string;
}

/**
 * Acknowledgment record for critical notifications
 */
export interface NotificationAcknowledgment {
  /** Acknowledgment ID */
  id: string;
  
  /** Notification ID */
  notificationId: string;
  
  /** User who acknowledged */
  userId: string;
  
  /** ISO timestamp */
  acknowledgedAt: string;
  
  /** Optional comment */
  comment?: string;
  
  /** IP address of acknowledger */
  ipAddress: string;
  
  /** User agent */
  userAgent: string;
}

// =============================================
// USER PREFERENCES
// =============================================

/**
 * Channel preference for a notification type
 */
export interface ChannelPreference {
  /** Notification type */
  type: NotificationType;
  
  /** Enabled channels */
  channels: NotificationChannel[];
  
  /** Override default priority (optional) */
  priority?: NotificationPriority;
  
  /** Muted until timestamp (temporary mute) */
  mutedUntil?: string;
}

/**
 * Email digest frequency options
 */
export type EmailDigestFrequency = 'Immediate' | 'Hourly' | 'Daily' | 'Weekly';

/**
 * User notification preferences
 */
export interface UserNotificationPreferences {
  /** User ID */
  userId: string;
  
  /** Global notification enable/disable */
  enabled: boolean;
  
  /** Per-type channel preferences */
  typePreferences: ChannelPreference[];
  
  /** Email digest preference */
  emailDigest: {
    enabled: boolean;
    frequency: EmailDigestFrequency;
    preferredTime?: string; // HH:mm UTC
  };
  
  /** Quiet hours (no push notifications) */
  quietHours?: {
    enabled: boolean;
    start: string; // HH:mm
    end: string;   // HH:mm
    timezone: string;
  };
  
  /** Categories to always include/exclude */
  categoryOverrides?: {
    category: NotificationCategory;
    enabled: boolean;
  }[];
  
  /** Last updated timestamp */
  updatedAt: string;
}

// =============================================
// ESCALATION CONFIGURATION
// =============================================

/**
 * Escalation target type
 */
export interface EscalationTarget {
  type: 'Role' | 'User';
  value: string; // Role name or User ID
}

/**
 * Escalation rule for unacknowledged critical notifications
 */
export interface EscalationRule {
  /** Rule ID */
  id: string;
  
  /** Rule name for display */
  name: string;
  
  /** Notification types this rule applies to */
  notificationTypes: NotificationType[];
  
  /** Time until escalation (minutes) */
  escalationDelayMinutes: number;
  
  /** Escalation targets (roles or specific users) */
  escalateTo: EscalationTarget[];
  
  /** Maximum escalation levels */
  maxEscalations: number;
  
  /** Whether to include original recipient */
  includeOriginalRecipient: boolean;
  
  /** Whether rule is active */
  isActive: boolean;
}

/**
 * Escalation event record
 */
export interface EscalationEvent {
  /** Escalation ID */
  id: string;
  
  /** Original notification ID */
  notificationId: string;
  
  /** Original recipient who didn't acknowledge */
  originalUserId: string;
  
  /** Escalation level (1, 2, 3...) */
  level: number;
  
  /** Users escalated to */
  escalatedToUserIds: string[];
  
  /** ISO timestamp */
  escalatedAt: string;
  
  /** Rule that triggered escalation */
  ruleId: string;
}

// =============================================
// NOTIFICATION TEMPLATES
// =============================================

/**
 * Channel-specific template content
 */
export interface TemplateContent {
  channel: NotificationChannel;
  title: string;      // Supports {{variable}} placeholders
  body: string;       // Supports {{variable}} and Markdown
  actionLabel?: string;
}

/**
 * Template for notification content
 */
export interface NotificationTemplate {
  /** Template ID */
  id: string;
  
  /** Notification type this template is for */
  type: NotificationType;
  
  /** Channel-specific content */
  content: TemplateContent[];
  
  /** Default priority */
  defaultPriority: NotificationPriority;
  
  /** Default channels */
  defaultChannels: NotificationChannel[];
  
  /** Whether acknowledgment is required by default */
  requiresAcknowledgment: boolean;
  
  /** Default expiration (hours) */
  expirationHours?: number;
}

// =============================================
// API REQUEST/RESPONSE TYPES
// =============================================

/**
 * Request to create a notification
 */
export interface CreateNotificationRequest {
  type: NotificationType;
  targetUserIds: string[];
  evidenceId?: string;
  caseNumber?: string;
  templateVariables?: Record<string, string>;
  customTitle?: string;
  customBody?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Delivery state per channel
 */
export interface ChannelDeliveryState {
  channel: NotificationChannel;
  state: NotificationState;
}

/**
 * Notification with user-specific state
 */
export interface NotificationWithState extends Notification {
  /** Delivery states per channel */
  deliveryStates: ChannelDeliveryState[];
  
  /** Whether user has read this notification */
  isRead: boolean;
  
  /** Whether user has acknowledged (if required) */
  isAcknowledged: boolean;
  
  /** Whether acknowledgment is required */
  requiresAcknowledgment: boolean;
  
  /** Acknowledgment details (if acknowledged) */
  acknowledgment?: NotificationAcknowledgment;
}

/**
 * Query parameters for listing notifications
 */
export interface ListNotificationsQuery {
  page?: number;
  pageSize?: number;
  category?: NotificationCategory;
  priority?: NotificationPriority;
  unreadOnly?: boolean;
  startDate?: string;
  endDate?: string;
}

/**
 * Notification list response
 */
export interface NotificationListResponse {
  notifications: NotificationWithState[];
  totalCount: number;
  unreadCount: number;
  unacknowledgedCriticalCount: number;
  pageSize: number;
  pageNumber: number;
}

/**
 * Batch notification summary
 */
export interface NotificationBatchSummary {
  batchId: string;
  totalNotifications: number;
  successCount: number;
  failureCount: number;
  pendingCount: number;
  createdAt: string;
  completedAt?: string;
}

/**
 * Request to acknowledge a notification
 */
export interface AcknowledgeNotificationRequest {
  comment?: string;
}

/**
 * Acknowledgment response
 */
export interface AcknowledgeNotificationResponse {
  success: boolean;
  acknowledgmentId: string;
  acknowledgedAt: string;
}

// =============================================
// AUDIT TYPES
// =============================================

/**
 * Notification audit event types
 */
export type NotificationAuditEventType = 
  | 'NotificationCreated'
  | 'NotificationDelivered'
  | 'NotificationRead'
  | 'NotificationAcknowledged'
  | 'NotificationEscalated'
  | 'NotificationFailed';

/**
 * Notification audit event for Log Analytics
 */
export interface NotificationAuditEvent {
  eventType: NotificationAuditEventType;
  notificationId: string;
  notificationType: NotificationType;
  userId: string;
  userRole: string;
  channel?: NotificationChannel;
  evidenceId?: string;
  caseNumber?: string;
  timestamp: string;
  ipAddress?: string;
  metadata?: Record<string, unknown>;
}

// =============================================
// HELPER CONSTANTS
// =============================================

/**
 * Notification types that require acknowledgment by default
 */
export const CRITICAL_NOTIFICATION_TYPES: NotificationType[] = [
  'EVIDENCE_HASH_MISMATCH',
  'LEGAL_HOLD_APPLIED',
  'LEGAL_HOLD_EXPIRING',
  'DISPOSITION_REVIEW_REQUIRED',
  'SECURITY_UNAUTHORIZED_ACCESS',
  'SECURITY_DELETE_ATTEMPT',
  'SECURITY_WORM_VIOLATION',
  'SECURITY_AUDIT_ANOMALY',
];

/**
 * Notification types that should create CustodyChainEvents
 */
export const CUSTODY_CHAIN_NOTIFICATION_TYPES: NotificationType[] = [
  'EVIDENCE_HASH_MISMATCH',
  'LEGAL_HOLD_APPLIED',
  'LEGAL_HOLD_RELEASED',
  'EVIDENCE_SHARED',
  'DISCOVERY_REQUEST_APPROVED',
];

/**
 * Default notification channels by priority
 */
export const DEFAULT_CHANNELS_BY_PRIORITY: Record<NotificationPriority, NotificationChannel[]> = {
  Low: ['InApp'],
  Medium: ['InApp', 'Email'],
  High: ['InApp', 'Email'],
  Critical: ['InApp', 'Email', 'Push'],
};

/**
 * Category display information
 */
export const NOTIFICATION_CATEGORY_INFO: Record<NotificationCategory, { label: string; icon: string; color: string }> = {
  Evidence: { label: 'Evidence', icon: 'FileText', color: 'blue' },
  Case: { label: 'Case', icon: 'Briefcase', color: 'purple' },
  LegalHold: { label: 'Legal Hold', icon: 'Shield', color: 'red' },
  Discovery: { label: 'Discovery', icon: 'Share2', color: 'green' },
  Compliance: { label: 'Compliance', icon: 'Clock', color: 'orange' },
  System: { label: 'System', icon: 'AlertTriangle', color: 'gray' },
};

/**
 * Priority display information
 */
export const NOTIFICATION_PRIORITY_INFO: Record<NotificationPriority, { label: string; color: string; borderColor: string }> = {
  Low: { label: 'Low', color: 'gray', borderColor: 'border-l-gray-300' },
  Medium: { label: 'Medium', color: 'blue', borderColor: 'border-l-blue-400' },
  High: { label: 'High', color: 'yellow', borderColor: 'border-l-yellow-500' },
  Critical: { label: 'Critical', color: 'red', borderColor: 'border-l-red-600' },
};
